import React, {
    useContext, useMemo
} from 'react';
import { IntlContext, TranslatorContext } from '@jutro/locale';
import { SgNumberUtil } from 'saga-modules';
import SgQnbLobSpecificUtil from 'sg-qnb-lob-specific-util';
import messages from './SgPaymentHeader.messages';

function SgPaymentHeader(props) {
    const {
        paymentType,
        chosenPlan,
        showCPA,
        showDepositDetails,
        modelVM
    } = props;

    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);
    const totalPriceSection = useMemo(() => {
        return (
            <>
                <h2 className="sgPaymentDetailsTitle">
                    <span>{translator(messages.TotalPrice)}</span>
                    <span id="cardTotalCost">
                        <span>£</span>
                        <span>{SgNumberUtil.getPriceInteger(chosenPlan.total.amount)}</span>
                        <span>{SgNumberUtil.getPriceFraction(chosenPlan.total.amount)}</span>
                    </span>
                </h2>
            </>
        );
    }, [chosenPlan.total.amount]);

    const securityMessageBanner = useMemo(() => {
        return (
            <>
                <div className="sg-security-message-banner">
                    <div className="sg-secure-messages-panel">
                        <div className="sg-security-message-header">
                            <div className="sgSecurePaymentProvider" />
                            <h5 className="sg-security-message-title">{translator(messages.YourPayment)}</h5>
                        </div>
                        <p>{translator(messages.InformationProtected)}</p>
                    </div>
                </div>
            </>
        );
    }, [translator]);

    const annualPaymentTextSection = useMemo(() => {
        return (
            <>
                {paymentType.isAnnualUpfrontPayment && (
                    <p>
                        <span>{translator(messages.CreditDebitCardPayment)}</span>
                    </p>
                )}
                {paymentType.isAnnualDDPayment && (
                    <p>
                        <span>{translator(messages.AnnualDirectDebitPayment)}</span>
                    </p>
                )}
                {totalPriceSection}
                {!showCPA && !paymentType.isAnnualDDPayment && !paymentType.isAnnualUpfrontPayment && (
                    <div className="sgInstallments">
                        <p>
                            <span id="cardSinglePayment">{translator(messages.CardSinglePayment)}</span>
                        </p>
                    </div>
                )}
                {(showCPA || paymentType.isAnnualUpfrontPayment) && (
                    <div className="sgInstallments">
                        <p>
                            <span>{translator(messages.DoNotRefresh)}</span>
                        </p>
                    </div>
                )}
            </>
        );
    }, [showCPA, paymentType.isAnnualUpfrontPayment, paymentType.isAnnualDDPayment]);

    const numberOfInstallmentLeft = useMemo(() => {
        const message = paymentType.isQuarterlyDDPayment ? messages.FollowedByQuarterlyInstallments : messages.FollowedByInstallments;
        const messageParams = {
            numberOfInstallmentLeft: chosenPlan.numberOfInstallments_Ext > 1 ? ((chosenPlan.numberOfInstallments_Ext) - 1).toString() : ''
        };
        return translator(message, messageParams);
    }, []);

    const monthlyPaymentTextSection = useMemo(() => {
        if (!paymentType.isMonthlyPayment) {
            return (<></>);
        }
        return (
            <div className="sgFlexPaymentHeader">
                <div>
                    {totalPriceSection}
                    <div className="sgInstallments">
                        <p id="ddiAPR">
                            <span><strong>{translator(messages.PleaseNote)}</strong></span>
                            <span>
                                {translator(
                                    SgQnbLobSpecificUtil.getPaymentsHeaderMessage(modelVM),
                                    SgQnbLobSpecificUtil.getPaymentsHeaderMessageParameters(chosenPlan)
                                )}
                            </span>
                        </p>
                    </div>
                    <div className="sgPaymentDDOutline">
                        <h3>
                            <span>{translator(messages.FirstDirectDebit)}</span>
                            <span id="ddiFirstInstallment">
                                {chosenPlan.firstInstallment_Ext
                                    ? SgNumberUtil.getCurrencyValue(intl, chosenPlan.firstInstallment_Ext) : ''}
                            </span>
                        </h3>
                        <h3>
                            <span>{numberOfInstallmentLeft}</span>
                            <span id="ddiInstallment">{SgNumberUtil.getCurrencyValue(intl, chosenPlan.installment)}</span>
                        </h3>
                    </div>
                </div>
                {securityMessageBanner}
            </div>
        );
    }, [paymentType]);

    const monthlyUpfrontPaymentTextSection = useMemo(() => {
        if (!paymentType.isMonthlyUpfrontPayment) {
            return (<></>);
        }
        return (
            <div className="sgFlexPaymentHeader">
                <div>
                    {totalPriceSection}
                    {!showCPA && (
                        <div>
                            <h3>
                                <span>{translator(messages.FirstDirectDebitMixed)}</span>
                                <span id="ddiFirstInstallment">
                                    {chosenPlan.firstInstallment_Ext
                                        ? SgNumberUtil.getCurrencyValue(intl, chosenPlan.firstInstallment_Ext) : ''}
                                </span>
                            </h3>
                            <h3>
                                {!showDepositDetails && (
                                    <>
                                        <span>{numberOfInstallmentLeft}</span>
                                        <span id="ddiInstallment">{SgNumberUtil.getCurrencyValue(intl, chosenPlan.installment)}</span>
                                    </>
                                )}
                                {showDepositDetails && (
                                    <>
                                        <span>{translator(messages.MixedStepTwo)}</span>
                                        <span id="cardDownPayment">{SgNumberUtil.getCurrencyValue(intl, chosenPlan.downPayment)}</span>
                                    </>
                                )}
                            </h3>
                        </div>
                    )}
                    {showCPA && (
                        <div className="sgInstallments">
                            <p>
                                <span>{translator(messages.DoNotRefresh)}</span>
                            </p>
                        </div>
                    )}
                </div>
                {securityMessageBanner}
            </div>
        );
    }, [showCPA, showDepositDetails]);

    return (
        <div className="sgPaymentTotals">
            {paymentType.isAnnualPayment && (
                <>
                    { annualPaymentTextSection }
                </>

            )}
            {paymentType.isMonthlyPayment && (
                <>
                    { monthlyPaymentTextSection }
                </>

            )}
            {paymentType.isMonthlyUpfrontPayment && (
                <>
                    { monthlyUpfrontPaymentTextSection }
                </>

            )}
        </div>
    );
}
export default SgPaymentHeader;
